import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { formatDate } from '@roc/feature-utils';
import { FileUpload } from '@roc/ui';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { InsuranceInformationBaseStore } from './insuranceInformationBaseStore';

const form = {
  fields: {
    insuranceQuoteDetailId: {
      value: '',
      error: null,
      rule: '',
    },
    insuranceAgentName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Insurance Agent Name is required.'
    },
    insuranceAgentPhone: {
      value: '',
      error: null,
      rule: 'required|phone',
      message: 'The Insurance Agent Phone is required.'
    },
    insuranceAgentEmail: {
      value: '',
      error: null,
      rule: 'required|email',
      message: 'The Insurance Agent Email is required.'
    },
    insuranceAgentAddress: {
      value: '',
      error: null,
      rule: [{ required_if: ['__isInternal__', true] }],
      message: 'The Insurance Agent Address field is required when isInternal is true.'
    },
    policyNumber: {
      value: '',
      error: null,
      rule: '',
    },
    insuranceEffectiveDate: {
      value: null,
      error: null,
      rule: '',
    },
    insuranceExpiryDate: {
      value: null,
      error: null,
      rule: '',
    },
    carrier: {
      value: '',
      error: null,
      rule: '',
    },
    carrierNaic: {
      value: '',
      error: null,
      rule: '',
    },
    coverageAmount: {
      value: null,
      error: null,
      rule: ''
    },
    insurancePremium: {
      value: null,
      error: null,
      rule: 'required',
      message: 'The Insurance Premium is required.'
    },
    paidAlready: {
      value: null,
      error: null,
      rule: 'required',
      message: 'The Paid Already is required.'
    },
    remainingAmount: {
      value: null,
      error: null,
      rule: '',
    },
    properties: {
      value: [],
      error: null,
      rule: 'required',
    },
    __isInternal__: {
      value: null,
      error: null,
      rule: '',
    },
    propertyCoverage: {
      value: false,
      error: null,
      rule: '',
    },
    generalLiabilityCoverage: {
      value: false,
      error: null,
      rule: '',
    },
    floodCoverage: {
      value: false,
      error: null,
      rule: '',
    },
    packageCoverage: {
      value: null,
      error: null,
      rule: '',
    },
    automatedResolutions: {
      value: null,
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class InsuranceQuoteFormStore extends FormStore {
  documents: FileUpload[];
  documentIsValid: boolean;
  isInternal: boolean;
  insuranceInformationStore: InsuranceInformationBaseStore;
  linkedAgentId: number;

  constructor(isInternal: boolean, insuranceInformationStore: InsuranceInformationBaseStore, globalStore: GlobalStore) {
    super(form, globalStore);
    this.isInternal = isInternal;
    this.insuranceInformationStore = insuranceInformationStore;
    makeObservable(this, {
      documents: observable,
      documentIsValid: observable,
      setDocumentIsvalid: action,
      setDocuments: action,
      paymentType: computed,
      packageSelected: computed,
      getInsuranceQuote: action,
      isInternal: observable,
      setPortalToForm: action,
      setAutomatedResolution:action,
      linkedAgentId: observable,
      isAgentLinked: computed,
      applyDirectoryAgent: action,
      unlinkDirectoryAgent: action,
    });
    reaction(
      () => this.packageSelected,
      (selected) => {

        if (selected) {
          this.onFieldChange('propertyCoverage', true);
          this.onFieldChange('generalLiabilityCoverage', true);
        } else {
          this.onFieldChange('propertyCoverage', false);
          this.onFieldChange('generalLiabilityCoverage', false);
        }
      }
    );
    this.setDefaults();
  }

  reset() {
    super.reset();
    this.setDefaults();
  }

  private setDefaults() {
    this.setDocuments([]);
    this.setDocumentIsvalid(null);
    this.linkedAgentId = null;
  }

  get agentDirectoryStore() {
    return this.insuranceInformationStore.agentDirectoryStore;
  }

  get isAgentLinked() {
    return !!this.linkedAgentId;
  }

  applyDirectoryAgent(agent: {
    insuranceAgentId?: number;
    name: string;
    email: string;
    phone: string;
    address: string;
  }) {
    this.linkedAgentId = agent.insuranceAgentId ?? null;
    this.onFieldChange('insuranceAgentName', agent.name);
    this.onFieldChange('insuranceAgentEmail', agent.email);
    this.onFieldChange('insuranceAgentPhone', agent.phone);
    this.onFieldChange('insuranceAgentAddress', agent.address);
  }

  unlinkDirectoryAgent() {
    this.linkedAgentId = null;
    this.agentDirectoryStore.clearSelection();
  }

  setDocuments(files: FileUpload[]) {
    this.documents = files;
  }

  setDocumentIsvalid(isValid: boolean) {
    this.documentIsValid = isValid;
  }

  onFieldChange(name, value) {

    super.onFieldChange(name, value);

    if (name == 'insurancePremium' || name == 'paidAlready') {
      const remainingAmount = this.form.fields.insurancePremium.value - this.form.fields.paidAlready.value;
      super.onFieldChange('remainingAmount', remainingAmount);
    }
  }

  get paymentType() {
    return this.form.fields.remainingAmount.value == 0 ? 'Paid Already' : 'Paid Through HUD';
  }

  get packageSelected() {
    return this.form.fields.packageCoverage.value === true;
  }

  // Backend now holds agent info as its own nested DTO (InsuranceQuoteAgent)
  // instead of flat insuranceAgentName/Email/Phone/Address + insuranceAgentId
  // fields — the 4 flat fields stay as-is on the form itself (that's what
  // the field components are bound to), just repacked into `agent` here at
  // the request boundary. See loadForm() below for the reverse, on read.
  getInsuranceQuote() {
    const {
      insuranceAgentName,
      insuranceAgentEmail,
      insuranceAgentPhone,
      insuranceAgentAddress,
      ...formValues
    } = this.getFormValues();
    const quote = {
      ...formValues,
      properties: this.getValue('properties').includes('All') ? this.insuranceInformationStore.propertiesOptions
        .map(option => option.value).filter(value => value != 'All') : this.getValue('properties'),
      paymentType: this.paymentType,
      insuranceEffectiveDate: formatDate(formValues.insuranceEffectiveDate, 'MM/dd/yyyy'),
      insuranceExpiryDate: formatDate(formValues.insuranceExpiryDate, 'MM/dd/yyyy'),
      documents: this.documents,
      agent: {
        insuranceAgentId: this.linkedAgentId,
        name: insuranceAgentName,
        email: insuranceAgentEmail,
        phone: insuranceAgentPhone,
        address: insuranceAgentAddress,
      },
    }
    return quote;
  }

  // Un-nests the backend's `agent` object back into the form's flat fields
  // (and restores linkedAgentId) — the mirror image of getInsuranceQuote().
  loadForm(quote: any) {
    const agent = quote?.agent ?? {};
    super.loadForm({
      ...quote,
      insuranceAgentName: agent.name,
      insuranceAgentEmail: agent.email,
      insuranceAgentPhone: agent.phone,
      insuranceAgentAddress: agent.address,
    });
    this.linkedAgentId = agent.insuranceAgentId ?? null;
  }

  setPortalToForm() {
    this.onFieldChange('__isInternal__', this.isInternal);
  }

  setAutomatedResolution(value,selectedQuote) {
    this.onFieldChange('automatedResolutions', value);

  }

  getRequestElmsureQuoteValues() {
    return {
      propertyCoverage: this.getValue('propertyCoverage'),
      generalLiabilityCoverage: this.getValue('generalLiabilityCoverage'),
      floodCoverage: this.getValue('floodCoverage'),
      properties: this.getValue('properties'),
    }
  }

}